import { motion } from 'framer-motion';
import {
  Award,
  Zap,
  Shield,
  ArrowRight,
  Layers,
  CheckCircle,
  Target,
  TrendingUp,
  Users,
  BarChart3,
  AlertCircle,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

export default function CoreCompetenciesPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const competencyTests = [
    {
      icon: Award,
      title: 'Valuable',
      description: "Provides significant value to customers—they're willing to pay for it",
      questions: [
        'Does this create real customer value?',
        'Would customers pay more for this?',
        'Does this solve an important problem?',
      ],
      example: "Amazon's logistics speed delivers value customers notice and pay Prime fees for",
    },
    {
      icon: Shield,
      title: 'Rare',
      description: 'Difficult for competitors to replicate—not easily bought or copied',
      questions: [
        'How many competitors can do this?',
        'Can this be bought off the shelf?',
        'How long would it take to copy?',
      ],
      example: "Netflix's recommendation algorithm took years and billions of data points to build",
    },
    {
      icon: Zap,
      title: 'Inimitable',
      description: 'Hard to imitate due to complexity, culture, history, or causal ambiguity',
      questions: [
        "Why can't competitors copy this easily?",
        'What makes this unique to us?',
        'Is this protected by patents, culture, or complexity?',
      ],
      example:
        "Pixar's creative culture—competitors can't just hire people and replicate the magic",
    },
    {
      icon: Target,
      title: 'Organized to Exploit',
      description:
        'Your organization is structured and resourced to actually leverage this capability',
      questions: [
        'Do we have systems to leverage this?',
        'Are resources allocated to this?',
        'Does our structure support this?',
      ],
      example:
        "Apple's design competency is supported by vertical integration and premium pricing power",
    },
  ];

  const companyExamples = [
    {
      company: 'Apple',
      competencies: ['Design Excellence', 'Ecosystem Integration', 'Brand & Retail Experience'],
      analysis:
        "Apple doesn't compete on specs or price. Their core competency is creating beautifully designed products that work seamlessly together within a premium ecosystem. This is valuable (customers pay premium), rare (few achieve Apple's design quality), inimitable (requires culture + taste + supply chain mastery), and organized (entire company structure supports it).",
      leverage:
        'Applied across iPhone, Mac, iPad, Watch, Services—same design philosophy, same ecosystem lock-in',
    },
    {
      company: 'Southwest Airlines',
      competencies: ['Operational Efficiency', 'Point-to-Point Network', 'Employee Culture'],
      analysis:
        "Southwest's competency isn't \"flying planes\"—everyone does that. It's running the most efficient operation in the industry through single aircraft type (Boeing 737), 10-minute gate turns, point-to-point routing, and a culture where employees genuinely care. Competitors have tried to copy for decades but can't replicate the culture.",
      leverage:
        'Enables lowest costs, enabling lowest fares, enabling highest load factors—virtuous cycle',
    },
    {
      company: 'Tesla',
      competencies: ['EV Powertrain Technology', 'Over-the-Air Software', 'Direct Sales Model'],
      analysis:
        "Tesla's lead in battery technology, motor efficiency, and software-defined vehicles creates competency moats. Traditional automakers have capital but lack the 10+ year head start in EV-specific design and software culture. Direct sales eliminates dealer markup and provides customer data.",
      leverage:
        'Applied to cars, trucks, semi-trucks, energy storage, solar—same core powertrain and software',
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Internal Assessment', path: '/internal-assessment' },
              { label: 'Core Competencies' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Layers className="w-4 h-4 mr-2" />
            Internal Assessment Tool 2 of 3
          </span>
        }
        title="Find the capabilities worth building around"
        subtitle="Discover what your business does well that customers value and competitors find hard to copy. Use the VRIO framework to distinguish useful skills from strategic advantages."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Assess your capabilities
        </Link>
      </PageHero>

      {/* Why Competencies Matter */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">
                Know which strengths can support your strategy
              </h2>
              <p className="text-xl text-gray-600">
                Being good at something does not always make it an advantage. Examine customer
                value, scarcity, imitation, and organizational support before making it central to
                your plan.
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: Shield,
                  title: 'Defensible Competitive Advantage',
                  description:
                    'True competencies are hard to copy, creating sustainable moats. Competitors can replicate features, pricing, even marketing—but not organizational capabilities built over years.',
                  impact: 'Protects market position and premium pricing power',
                },
                {
                  icon: TrendingUp,
                  title: 'Foundation for Growth',
                  description:
                    "Competencies can be leveraged across products, markets, and customers. Amazon's logistics competency enabled retail, then AWS, then third-party fulfillment.",
                  impact: 'Efficient diversification and growth opportunities',
                },
                {
                  icon: Target,
                  title: 'Strategic Focus',
                  description:
                    "Knowing your competencies clarifies what to pursue and what to avoid. Stay in areas where you have advantage; avoid where you're merely adequate.",
                  impact: 'Resource allocation and strategic clarity',
                },
                {
                  icon: Users,
                  title: 'Talent Magnet',
                  description:
                    'People want to work where they can develop world-class skills. Clear competencies attract and retain A-players who want to be the best.',
                  impact: 'Competitive talent acquisition and retention',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <item.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold">{item.title}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-xs text-gray-700">
                      <span className="font-semibold text-green-700">Impact:</span> {item.impact}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VRIO Framework */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">The VRIO Framework</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Test if a capability is truly a core competency with four questions: Valuable, Rare,
              Inimitable, Organized
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {competencyTests.map((test, i) => (
              <motion.div
                key={i}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="public-card bg-white p-8 rounded-xl border border-gray-200"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-blue-600 p-3 rounded-lg">
                    <test.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold">{test.title}</h3>
                </div>
                <p className="text-gray-700 mb-4">{test.description}</p>
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Key Questions:</p>
                  <ul className="space-y-1">
                    {test.questions.map((q, j) => (
                      <li key={j} className="text-xs text-gray-600 flex items-start">
                        <CheckCircle className="w-3 h-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                        {q}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Example:</span> {test.example}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="public-card max-w-3xl mx-auto mt-8 p-6 bg-blue-50 rounded-xl border border-gray-200"
          >
            <p className="text-gray-700 text-center">
              <span className="font-semibold text-blue-700">Remember:</span> A capability must pass
              all four tests to be a true core competency. If it fails any test, it's an operational
              capability (necessary but not differentiating) or a weakness.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How to Identify */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">How to Identify Your Core Competencies</h2>
              <p className="text-xl text-gray-600">
                A systematic process to uncover and validate competencies
              </p>
            </motion.div>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Start with Customer Value',
                  description:
                    'Ask: What do customers consistently choose us for? What would they miss if we disappeared? Customer value reveals true competencies—internal perceptions are often wrong.',
                  tip: 'Interview 10-20 customers. Ask: "Why do you buy from us vs. competitors? What would you lose if we went away?" Patterns reveal competencies.',
                },
                {
                  step: '2',
                  title: 'Examine Consistent Wins',
                  description:
                    "Look at deals you've won over past 2-3 years. What was the common reason? If you consistently win on speed, innovation, or service—that's likely a competency.",
                  tip: 'Review win/loss analysis. If no clear pattern emerges, you may not have a true competency—just good execution in random areas.',
                },
                {
                  step: '3',
                  title: "Identify What's Hard to Copy",
                  description:
                    "Competitors copy features, pricing, messaging quickly. What haven't they copied despite trying? That's probably a competency. If it was easy to copy, they would have.",
                  tip: 'Ask: "What would a well-funded competitor struggle to replicate even with 2 years and $10M?" That\'s your competency.',
                },
                {
                  step: '4',
                  title: 'Look for Integration of Skills',
                  description:
                    "Core competencies aren't single skills—they're bundles. Apple's design competency integrates industrial design, UX, supply chain, retail, and brand. The integration is what's hard to copy.",
                  tip: 'If you can name a single department that "owns" the capability, it\'s not a core competency—it\'s a function. Competencies span the organization.',
                },
                {
                  step: '5',
                  title: 'Test with VRIO Framework',
                  description:
                    'For each candidate competency, rigorously apply Valuable, Rare, Inimitable, Organized tests. Most "competencies" fail 2-3 tests and are actually just operational capabilities.',
                  tip: "Be honest and ruthless. Most companies have 2-4 true core competencies. If you identify 10+, you're being too generous.",
                },
                {
                  step: '6',
                  title: 'Validate Sustainability',
                  description:
                    "True competencies take 5-10+ years to build and decay slowly. If you built it in 6 months or competitors are catching up quickly, it's not a core competency.",
                  tip: 'Ask: "Could we lose this in 18 months of neglect?" If yes, it\'s fragile and not truly core. Real competencies have momentum and organizational memory.',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-blue-700">Pro Tip:</span> {item.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Real Examples */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">
              Core Competency Examples from Leading Companies
            </h2>
            <p className="text-xl text-gray-600">
              See how competencies create sustainable advantage
            </p>
          </motion.div>
          <div className="space-y-8 max-w-5xl mx-auto">
            {companyExamples.map((ex, i) => (
              <motion.div
                key={i}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="public-card bg-white p-8 rounded-xl border border-gray-200"
              >
                <h3 className="text-2xl font-bold mb-4">{ex.company}</h3>
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Core Competencies:</p>
                  <div className="flex flex-wrap gap-2">
                    {ex.competencies.map((c, j) => (
                      <span
                        key={j}
                        className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg mb-3">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Analysis:</span> {ex.analysis}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-green-700">Leverage:</span> {ex.leverage}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common Core Competency Mistakes</h2>
              <p className="text-xl text-gray-600">
                Avoid these traps that confuse competency with capability
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Confusing Table Stakes with Competencies',
                  description:
                    '"We\'re good at customer service" isn\'t a competency if everyone in your industry provides decent service. Competencies must differentiate, not just meet minimum standards.',
                  fix: "Test: Would customers choose us primarily for this capability? If not, it's table stakes, not a competency.",
                },
                {
                  mistake: 'Listing Too Many "Competencies"',
                  description:
                    'Claiming 10-15 core competencies. If everything is core, nothing is. True competencies are rare by definition—most companies have 2-4 maximum.',
                  fix: 'Force prioritization: If you had to defend just 3 capabilities as truly differentiating and sustainable, what would they be?',
                },
                {
                  mistake: 'Describing What You Do, Not Why You Win',
                  description:
                    '"We develop software" describes activity, not competency. "We ship features 3x faster than industry average" explains competitive advantage.',
                  fix: 'Frame competencies as comparative advantages: "We do X better/faster/cheaper than competitors because of Y unique capability."',
                },
                {
                  mistake: 'Identifying Assets, Not Capabilities',
                  description:
                    'Having great technology or talented people are assets, not competencies. Competency is how you leverage those assets to create unique value.',
                  fix: 'Ask: "If competitor bought our technology or hired our people, could they replicate our advantage?" If yes, it\'s an asset. If no, it\'s a competency.',
                },
                {
                  mistake: 'Ignoring "Organized to Exploit"',
                  description:
                    'You have the skills but not the structure, processes, or culture to actually leverage them. Potential competency ≠ realized competency.',
                  fix: "Validate: Are resources allocated? Are systems in place? Can we actually execute on this consistently? If not, it's aspirational.",
                },
                {
                  mistake: 'Static View of Competencies',
                  description:
                    'Identifying competencies once and assuming they last forever. Markets evolve, technologies change, and competencies can decay or become commoditized.',
                  fix: 'Review annually: Are our competencies still valuable? Rare? Inimitable? Are we investing to maintain and extend them?',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">
                Build your strategy on capabilities you can defend
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Identify the candidates</h3>
                <p className="text-gray-600">
                  List the skills, systems, and combinations of expertise that create value for
                  customers.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Test the advantage</h3>
                <p className="text-gray-600">
                  Apply VRIO to separate essential capabilities from those that may set your company
                  apart.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Invest with purpose</h3>
                <p className="text-gray-600">
                  Decide which competencies to strengthen and where they support your strategic
                  choices.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Strategic Tools</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/internal-assessment/swot-analysis"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <CheckCircle className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-blue-600">SWOT Analysis</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Identify strengths that could be core competencies
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/internal-assessment/resource-audit"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <BarChart3 className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-purple-600">Resource Audit</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Assess resources that enable competencies
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/strategy-formulation/competitive-strategy"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Shield className="w-8 h-8 text-indigo-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-indigo-600">Competitive Strategy</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Leverage competencies for competitive advantage
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/internal-assessment/swot-analysis"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Previous: SWOT Analysis
              </Link>
              <Link
                to="/internal-assessment/resource-audit"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next: Resource Audit <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">
            Assess your capabilities in StratPlan
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Decide which competencies to strengthen and where they support your strategic choices.
            Start with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Assess your capabilities <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
